/**
 * ─── Generic Mutation Draft Types ─────────────────────────────────────────────
 * Source-agnostic types powering the calendar mutation system.
 * Any feature (scheduler agent, Dauertermin, manual edits) can produce mutations
 * that flow through the same Generator → Adapter → DraftStore → Calendar pipeline.
 */

import { Scope, ScopeKey } from "../common/scopeUtils";

export interface MutationScope extends Scope {
  patientId: string;
  locationId: string;
}

// ─── Draft Source (discriminated union) ──────────────────────────────────────

export type DraftSource =
  | { type: "scheduler-agent"; uaId: string }
  | { type: "dauertermin"; dauerterminId: string }
  | { type: "manual"; description?: string };

// ─── Mutation Request (generic input to the generator) ───────────────────────

/**
 * A source-agnostic request to generate a calendar mutation.
 * Any feature creates one of these, and the generator figures out
 * where/how to place it on the calendar.
 */
export interface MutationRequest {
  /** Unique ID for this request (used as draft key) */
  id: string;
  /** Required duration in minutes */
  durationMinutes: number;
  /** Preferred therapist (optional constraint) */
  preferredTherapistId?: string;
  /** Target date ISO string, e.g. "2026-04-30" (defaults to selected calendar date) */
  targetDate?: string;
  /** Human-readable label for display, e.g. "Müller · KG 20min" */
  label: string;
  /** Where this request originated */
  source: DraftSource;

  dayStr: string; // action group daystr
}

export type DrivingTimeAction = "create" | "update" | "delete";

/** Describes a driving-time slot change proposed by the rescheduling agent. */
export interface DrivingTimeMutation {
  /** UUID of existing driving slot (for update/delete) */
  id?: string;
  /** Action: create a new slot, update an existing one, or delete it */
  action: DrivingTimeAction;
  /** Groups this change with the placement it belongs to */
  collectionId?: string;
  /** UUID of therapist */
  therapistId: string;
  therapistName: string;
  /** Start datetime ISO (for create/update) */
  start?: string;
  /** End datetime ISO (for create/update) */
  end?: string;
  /** Duration in minutes */
  durationMinutes?: number;
}

// ─── Shift Mutation ──────────────────────────────────────────────────────────

/** Describes how an existing appointment's timeslot must shift. */
export interface ShiftMutation {
  appointmentId: string;
  timeSlotId: string;
  /** Human-readable label, e.g. "Müller · KG" */
  label: string;
  originalStart: string;
  originalEnd: string;
  originalTherapistId: string;
  originalTherapistName: string;
  newStart: string;
  newEnd: string;
  newTherapistId: string;
  newTherapistName: string;
  patientId?: string;
}

// ─── Placement Mutation ──────────────────────────────────────────────────────

/** Describes where a new appointment gets placed. */
export interface PlacementMutation {
  start: string;
  end: string;
  durationMinutes: number;
  /** TimeSlot ID for reading display data from cache */
  timeSlotId?: string;
  /** Appointment ID (for action-group context) */
  appointmentId?: string;
  /** Therapist user ID */
  therapistId: string;
  /** Therapist display name */
  therapistName: string;
  patientId?: string;
}

// ─── Mutation Plan ───────────────────────────────────────────────────────────

/**
 * A complete mutation plan: shift N existing appointments, then place new ones.
 * This is the output of the generator (or backend).
 * One card per action group — contains all placements/shifts/skips for the group.
 */
export interface MutationPlan {
  shifts: ShiftMutation[];
  placements: PlacementMutation[];
  /** Driving-time slot changes associated with this plan */
  drivingTimes?: DrivingTimeMutation[];
  /** Appointments that could not be placed in this group */
  skipped?: FailedPlacement[];
}

// ─── Failed Placement ────────────────────────────────────────────────────────

/**
 * Represents an appointment the agent could not place.
 */
export interface FailedPlacement {
  appointmentId: string;
  timeSlotId: string;
  reason: string;
  patientId?: string;
}

export function isFailedPlacement(draft: MutationDraft): draft is FailedPlacement {
  return "reason" in draft && !("start" in draft) && !("placement" in draft);
}

// ─── Draft (what the store holds per entry) ──────────────────────────────────

export type MutationDraft = MutationPlan | FailedPlacement;

/** Full draft entry with metadata. */
export interface DraftEntry {
  /** The mutation plan or simple placement */
  draft: MutationDraft;
  /** Where this draft originated */
  source: DraftSource;
  /** The original request (needed for regeneration) */
  request: MutationRequest;
}

// ─── Draft Calendar Event (flat shape for calendar rendering) ────────────────

export type DraftEventType = "placement" | "shift" | "driving-time";

export interface DraftCalendarEvent {
  id: string;
  type: DraftEventType;
  /** Which draft this event belongs to (generic key) */
  draftId: string;
  therapistId: string;
  therapistName: string;
  start: string;
  end: string;
  label: string;
  /** Only for shifts — where the appointment used to be */
  originalStart?: string;
  originalEnd?: string;
  originalTherapistId?: string;
  originalTherapistName?: string;
  appointmentId?: string;
  timeSlotId?: string;
  /** Only for driving-time events */
  drivingTimeAction?: DrivingTimeAction;
}

// ─── Scoping ────────────────────────────────────────────────────────────────
// Scope utilities are centralized in src/stores/common/scopeUtils.ts.
// Re-exported here for backward compatibility with existing imports.

/** Canonical string key derived from a MutationScope (backward compat alias). */
export type MutationScopeKey = ScopeKey;

// ─── Type guard ──────────────────────────────────────────────────────────────

export function isMutationPlan(d: MutationDraft): d is MutationPlan {
  return "shifts" in d && "placements" in d;
}
